import { alertError, toastError } from './alerts';

/**
 * Catálogo de erros, espelhando o `code` que o GlobalExceptionHandler devolve.
 *
 * O servidor já manda `message` pronta para o usuário; o `code` serve para
 * decidir *como* exibir e o que acrescentar. Cada entrada traz:
 *
 * - `titulo`  cabeçalho do modal;
 * - `dica`    o que fazer a seguir, quando a mensagem do servidor não diz;
 * - `modal`   true exige o clique do usuário, false mostra toast.
 *
 * Códigos ausentes daqui caem em {@link PADRAO} — é de propósito: um código novo
 * no back não deve virar tela em branco no front.
 */
export const CATALOGO_ERROS = {
    SENHA_INVALIDA: {
        titulo: 'Senha incorreta',
        dica: 'Confirme a senha e tente novamente.',
        modal: false,
    },
    AUTORIZACAO_INVALIDA: {
        titulo: 'Autorização expirada',
        dica: 'A autorização vale para uma única operação e por poucos minutos. Peça uma nova.',
        modal: true,
    },
    ACESSO_NEGADO: {
        titulo: 'Sem permissão',
        dica: 'Seu perfil não alcança esta operação. Fale com um administrador.',
        modal: true,
    },
    VALIDACAO: {
        titulo: 'Dados incompletos',
        dica: 'Revise os campos destacados.',
        modal: false,
    },
    CORPO_INVALIDO: {
        titulo: 'Dados inválidos',
        dica: 'Algum campo está em formato inesperado.',
        modal: false,
    },
    PARAMETRO_INVALIDO: {
        titulo: 'Parâmetro inválido',
        dica: '',
        modal: false,
    },
    ARGUMENTO_INVALIDO: {
        titulo: 'Operação inválida',
        dica: '',
        modal: false,
    },
    NAO_ENCONTRADO: {
        titulo: 'Registro não encontrado',
        dica: 'Ele pode ter sido removido por outro usuário. Atualize a lista.',
        modal: false,
    },
    INTEGRIDADE: {
        titulo: 'Registro em uso',
        // É o caso do 409 por chave estrangeira: o cadastro está amarrado a
        // outro registro. Excluir não é opção; desativar costuma ser.
        dica: 'Está vinculado a outro cadastro. Desative em vez de excluir, ou remova primeiro os registros que dependem dele.',
        modal: true,
    },
    CONFLITO: {
        titulo: 'Operação não permitida agora',
        dica: '',
        modal: true,
    },
    CONCORRENCIA: {
        titulo: 'Registro alterado por outro usuário',
        dica: 'Recarregue a página para ver a versão atual antes de salvar de novo.',
        modal: true,
    },
    ARQUIVO_GRANDE: {
        titulo: 'Arquivo muito grande',
        dica: 'Reduza o tamanho e envie novamente.',
        modal: false,
    },
    ERRO_INTERNO: {
        titulo: 'Erro no servidor',
        // A mensagem do servidor traz um código de referência que localiza o
        // stack trace no log — por isso exige clique, não toast de 3,5s.
        dica: 'Se o problema repetir, informe o código entre parênteses ao suporte.',
        modal: true,
    },
};

const PADRAO = { titulo: 'Não foi possível concluir', dica: '', modal: false };

/** Lê o corpo de erro do Axios, tolerando resposta em Blob já convertida. */
const corpoDoErro = (error) => {
    const data = error?.response?.data;
    return data && typeof data === 'object' ? data : null;
};

/**
 * Traduz um erro de requisição em título e mensagem prontos para exibir.
 *
 * @param error  erro do Axios
 * @param acao   o que estava sendo feito, em infinitivo e minúsculas
 *               ("excluir o plano"). Usado quando o servidor não respondeu nada
 *               aproveitável — falha de rede, por exemplo
 */
export function explicarErro(error, acao) {
    const corpo = corpoDoErro(error);
    const entrada = CATALOGO_ERROS[corpo?.code] || PADRAO;

    if (!error?.response) {
        return {
            titulo: 'Sem conexão',
            mensagem: `Não foi possível ${acao || 'concluir a operação'}: o servidor não respondeu.`,
            modal: false,
        };
    }

    const doServidor = corpo?.message;
    const partes = [
        doServidor || `Não foi possível ${acao || 'concluir a operação'}.`,
        entrada.dica,
    ].filter(Boolean);

    return { titulo: entrada.titulo, mensagem: partes.join(' '), modal: entrada.modal };
}

/**
 * Exibe o erro conforme o catálogo: modal quando o usuário precisa registrar o
 * que aconteceu, toast quando basta avisar.
 *
 * Use com `skipGlobalError: true` na requisição — sem isso o interceptor do
 * Axios também exibe o erro, e o usuário vê duas mensagens para a mesma falha.
 */
export function mostrarErro(error, acao) {
    const { titulo, mensagem, modal } = explicarErro(error, acao);
    if (modal) alertError(mensagem, titulo);
    else toastError(mensagem);
}
